import { PureQueryOptions } from '@apollo/client';
import { ExtraFieldsSupportedObject } from '../../../../components/ExtraFields';
import { ExtraFieldsFormId } from '../../../../components/ExtraFields/types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { isEqual, omit } from 'lodash';
import { ConfirmModalSimple } from '../../../../../../components/ConfirmModalSimple';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import { AutoBreaksRules } from '../../../../components/AutoBreaks/shared';
import { useUserInfo } from '../../../../context/UserInfo';
import {
  useDeleteTimeEntryChangeRequest,
  useSubmitTimeEntryChangeRequest,
} from '../../../../hooks/timeTracking';
import TimeEntryForm from '../components/TimeEntryForm';
import {
  isDirtyTimeEntryItem,
  TimeEntryItem,
  TimeEntryReadOnlyReason,
} from '../logic';
import {
  ProjectsDocument,
  TimeSheetDayProjectTimeEntryFragment,
  useSaveProjectTimeEntriesByDaysMutation,
} from '../../../../__generated__/graphql';
import {
  mapTimeEntryNotesChangedInput,
  mapToSubmitTimeEntryChangeRequestInput,
  mapProjectTimeEntriesForTimeEntry,
} from '../logic/mapping';
import {
  getValidationErrorMessages,
  validateTimeEntry,
} from '../logic/validation';
import { getInitialState, timeEntryStateReducer } from './state';
import { getSubmitDisabledTitle, isDirtyProjectEntries } from './helpers';
import TimeEntryDumb from './TimeEntryDumb';
import { useProjectEntriesValidator } from './useProjectEntriesValidator';
import { ExtraFieldsAreaSimpleRef } from '../../../../components/ExtraFields';
import styled from '@emotion/styled';
import { TimeEntryEmployeeButtons } from './TimeEntryEmployeeButtons';
import { TimeEntryCardLayout } from '../components/TimeEntryCardLayout';
import { useUpdateTimeEntryNotesMutation } from '../../../../__generated__/graphql';
import { CostCenterProps } from '..';
import {
  ProjectWithStatus,
  TimeEntryProps,
  ProjectTimeEntry,
} from '../../../Projects/types';
import { useTimeTrackingRestriction } from '../../../../hooks/timeTrackingSettings';
import { useToasts } from '../../../../../../context/Toast';
import { i18prefix, timesheetsRange } from '../../../Projects/helpers';
import { toISODateOnly } from '../../../../../../utils/dateUtils';
import { notEmpty } from '../../../../../../utils/array';
import {
  mapToProjectDayTimeTracked,
  mapToTimeEntryInput,
} from '../../../Projects/ProjectsTimesheets/mappings';
import { UNIQUE_ID_PREFFIX } from '../../../Projects/ProjectsTimesheets/helpers';
import { minToHoursInput } from '../components/ProjectDayTimeEntriesForm/utils';
import { useAutoBreaksLogic } from '../logic/useAutoBreaksLogic';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

type Props = {
  value: TimeEntryItem;
  allTimeEntries: TimeEntryItem[];
  costCenters: readonly CostCenterProps[];
  autoBreakRules: AutoBreaksRules | null;
  readonlyReasons: TimeEntryReadOnlyReason[];
  refetchQueriesForEmployeeMode?: PureQueryOptions[];
  onCancelCreatingNewTimeEntry: () => void;
  onAddNewTimeEntryClick: () => void;
  isLastEntry: boolean;
  projectTimeEntries?: ReadonlyArray<TimeSheetDayProjectTimeEntryFragment>;
  restrictedFutureEntries?: boolean;
  projects?: ProjectWithStatus[];
  onProjectTimeEntriesMinutesChange?: (
    timeEntryId: string | null,
    minutes: number,
  ) => void;
  timeEntriesForDay?: TimeEntryProps[];
  showTimeEntries?: boolean;
  isTimeTrackingWithProjects: boolean;
};

const TimeEntryEmployeeSmart: React.FC<Props> = ({
  value,
  allTimeEntries,
  costCenters,
  autoBreakRules,
  readonlyReasons,
  refetchQueriesForEmployeeMode,
  onCancelCreatingNewTimeEntry,
  onAddNewTimeEntryClick,
  isLastEntry,
  projectTimeEntries,
  restrictedFutureEntries,
  projects,
  onProjectTimeEntriesMinutesChange,
  timeEntriesForDay,
  showTimeEntries,
  isTimeTrackingWithProjects,
}) => {
  const [state, dispatch] = useReducer(
    timeEntryStateReducer,
    getInitialState(value),
  );

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [deleteTimeEntryChangeRequestId, setDeleteTimeEntryChangeRequestId] =
    useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const submitInFlight = useRef(false);
  const { ref: projectEntriesFormRef, validate: validateProjectEntries } =
    useProjectEntriesValidator();
  const extraFieldsRef = useRef<ExtraFieldsAreaSimpleRef>(null);
  const { id: meId } = useUserInfo();
  const { current, notesVisible, validationErrors, costCenterVisible } = state;

  const refetchQueries = [
    ...(refetchQueriesForEmployeeMode ?? []),
    {
      query: ProjectsDocument,
      variables: {
        from: moment(current.start.date)
          .startOf('day')
          .subtract(1, 'day')
          .toISOString(),
        to: moment(current.start.date).endOf('day').add(1, 'day').toISOString(),
      },
    },
  ];

  // we will remove refetchQueries after we merge all time entry graphql types into one (as we did for absences)
  // https://linear.app/flair/issue/CHR-538/merge-all-timeentry-graphql-types-into-one
  const [submitChangeRequest] = useSubmitTimeEntryChangeRequest({
    awaitRefetchQueries: true,
    /* eslint-disable-next-line no-restricted-syntax */
    refetchQueries: refetchQueries,
  });
  const [deleteTimeEntryChangeRequest] = useDeleteTimeEntryChangeRequest(
    value.timeSheetId,
  );

  const [updateTimeEntryNote] = useUpdateTimeEntryNotesMutation({
    awaitRefetchQueries: true,
    /* eslint-disable-next-line no-restricted-syntax */
    refetchQueries: refetchQueries,
  });

  const errorHandler = useMutationErrorHandler();

  const [currentProjectEntries, setCurrentProjectEntries] = useState<
    ProjectTimeEntry[]
  >([]);
  const initialProjectEntriesRef = useRef<ProjectTimeEntry[]>([]);
  const hasSetInitialRef = useRef(false);
  const [resetCounter, setResetCounter] = useState(0);

  const isNotesDirtyMemo = useMemo(
    () => value.notes !== current.notes,
    [value.notes, current.notes],
  );
  const shouldCreateChangeRequestMemo = useMemo(() => {
    const { breaks, ...currentVal } = current;

    if (value.isNew) {
      return true;
    }

    const initialWithoutNotes = omit(value, 'notes');
    const currentWithoutNotes = omit(currentVal, 'notes');

    return !isEqual(initialWithoutNotes, {
      ...currentWithoutNotes,
      breaks: breaks.map((item) => omit(item, 'isBreakAutoFixed')),
    });
  }, [value, current]);

  const isTimeEntryDirty = isDirtyTimeEntryItem(value, state.current);

  const isProjectEntriesDirty =
    hasSetInitialRef.current &&
    isDirtyProjectEntries(
      currentProjectEntries,
      initialProjectEntriesRef.current,
    );
  const isDirty = isTimeEntryDirty || isProjectEntriesDirty;

  const isDeleteChangeRequestAllowed = current.changeRequestEmployeeId === meId;

  const handleDeleteClick = useCallback(() => {
    setDeleteTimeEntryChangeRequestId(current.changeRequestId);
  }, [setDeleteTimeEntryChangeRequestId, current.changeRequestId]);

  const handleDeleteConfirmationClose = () => {
    setDeleteTimeEntryChangeRequestId(null);
  };

  const handleCancel = () => {
    if (value.isNew) {
      onCancelCreatingNewTimeEntry();
    } else {
      dispatch({ type: 'reset', initialTimeEntry: value });
      setResetCounter((prev) => prev + 1);
      hasSetInitialRef.current = false;
    }
  };

  const handleSubmit = async () => {
    const { current } = state;
    if (isProcessing || isSubmitting || submitInFlight.current) {
      return;
    }
    const errors = validateTimeEntry(current);
    if (errors.size !== 0) {
      return;
    }

    submitInFlight.current = true;
    try {
      const [extraValid, projectValid] = await Promise.all([
        extraFieldsRef.current?.triggerValidation() ?? true,
        validateProjectEntries(),
      ]);
      if (!extraValid || !projectValid) {
        return;
      }
      setIsSubmitting(true);
      dispatch({ type: 'validateTimeEntriesOverlap', allTimeEntries });
    } finally {
      submitInFlight.current = false;
    }
  };

  const handleNotesChanged = useCallback(() => {
    const { current } = state;
    const input = mapTimeEntryNotesChangedInput(current);
    setIsProcessing(true);
    updateTimeEntryNote({
      variables: {
        input,
      },
    })
      .catch(errorHandler)
      .finally(() => setIsProcessing(false));
  }, [state, updateTimeEntryNote, errorHandler]);

  const handleSubmitChangeRequest = useCallback(() => {
    const { current } = state;
    const projectEntriesToSave = currentProjectEntries
      .map((entry) =>
        mapToProjectDayTimeTracked(entry, moment(current.start.date)),
      )
      .filter(notEmpty)
      .map((mapped) => ({
        ...mapped,
        timesheetId: current.timeSheetId,
        dateId: toISODateOnly(new Date(mapped.date)),
      }));

    const hadProjectEntriesBefore = initialProjectEntriesRef.current.length > 0;
    const shouldIncludeProjectEntries =
      projectEntriesToSave.length > 0 || hadProjectEntriesBefore;
    const input = mapToSubmitTimeEntryChangeRequestInput(
      current,
      shouldIncludeProjectEntries ? projectEntriesToSave : undefined,
    );
    setIsProcessing(true);
    submitChangeRequest({
      variables: {
        input,
      },
    })
      .then(() => {
        initialProjectEntriesRef.current = currentProjectEntries;
        onCancelCreatingNewTimeEntry();
      })
      .catch(errorHandler)
      .finally(() => {
        setIsProcessing(false);
      });
  }, [
    state,
    submitChangeRequest,
    onCancelCreatingNewTimeEntry,
    errorHandler,
    currentProjectEntries,
  ]);

  useEffect(() => {
    if (isSubmitting && validationErrors.size === 0) {
      setIsSubmitting(false);
      if (!current.isNew && isNotesDirtyMemo) {
        handleNotesChanged();
      }
      if (shouldCreateChangeRequestMemo || isProjectEntriesDirty) {
        handleSubmitChangeRequest();
      }
    } else if (isSubmitting && validationErrors.size > 0) {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    validationErrors.size,
    current.isNew,
    isNotesDirtyMemo,
    shouldCreateChangeRequestMemo,
    isProjectEntriesDirty,
    handleNotesChanged,
    handleSubmitChangeRequest,
  ]);

  const handleDeleteConfirmationSubmit = useCallback(() => {
    if (!deleteTimeEntryChangeRequestId) {
      return;
    }
    setDeleteTimeEntryChangeRequestId(null);
    setIsProcessing(true);
    deleteTimeEntryChangeRequest({
      variables: {
        id: deleteTimeEntryChangeRequestId,
      },
    })
      .catch(errorHandler)
      .finally(() => {
        setIsProcessing(false);
      });
  }, [
    deleteTimeEntryChangeRequest,
    errorHandler,
    setIsProcessing,
    deleteTimeEntryChangeRequestId,
  ]);

  const readonly = readonlyReasons.length > 0;

  const {
    showRestrictedWarningModal,
    setShowRestrictedWarningModal,
    getTimeTrackingRestrictedReason,
  } = useTimeTrackingRestriction();
  const { addSuccess } = useToasts();
  const { t } = useTranslation();
  const [
    saveProjectTimeEntriesMutation,
    { loading: isSavingProjectTimeEntries },
  ] = useSaveProjectTimeEntriesByDaysMutation();

  const restrictionReason = getTimeTrackingRestrictedReason(current.start.date);

  const handleAddBreak = useCallback(() => {
    if (restrictionReason) {
      setShowRestrictedWarningModal(true);
    } else {
      dispatch({ type: 'newBreak', breakRules: autoBreakRules });
    }
  }, [
    restrictionReason,
    setShowRestrictedWarningModal,
    dispatch,
    autoBreakRules,
  ]);

  const mappedProjectTimeEntries = useMemo(
    () =>
      mapProjectTimeEntriesForTimeEntry(
        projectTimeEntries,
        current.timeEntryId,
      ),
    [projectTimeEntries, current.timeEntryId],
  );

  const totalProjectTimeEntriesMinutes = useMemo(() => {
    return mappedProjectTimeEntries.reduce(
      (total, entry) => total + (entry.minutes || 0),
      0,
    );
  }, [mappedProjectTimeEntries]);

  const [
    currentProjectTimeEntriesMinutes,
    setCurrentProjectTimeEntriesMinutes,
  ] = useState<number | null>(null);

  useEffect(() => {
    setCurrentProjectTimeEntriesMinutes(null);
    setCurrentProjectEntries(mappedProjectTimeEntries);
    initialProjectEntriesRef.current = mappedProjectTimeEntries;
    hasSetInitialRef.current = true;
  }, [mappedProjectTimeEntries, resetCounter]);

  const projectEntriesResetKey = useMemo(
    () => `${current.timeEntryId || 'new'}-${resetCounter}`,
    [current.timeEntryId, resetCounter],
  );

  const handleProjectTimeEntriesMinutesChangeInternal = useCallback(
    (minutes: number) => {
      setCurrentProjectTimeEntriesMinutes(minutes);
      onProjectTimeEntriesMinutesChange?.(current.timeEntryId, minutes);
    },
    [onProjectTimeEntriesMinutesChange, current.timeEntryId],
  );

  const handleSaveProjectTimeEntries = useCallback(
    (timesTracked: ProjectTimeEntry[]) => {
      const timeEntriesToSave = timesTracked
        .map((timeTracked) => {
          const mapped = mapToProjectDayTimeTracked(
            timeTracked,
            moment(current.start.date),
          );
          if (mapped && !mapped.timeEntryId && current.timeEntryId) {
            const isNewEntry =
              !timeTracked.id || timeTracked.id.includes(UNIQUE_ID_PREFFIX);
            if (isNewEntry) {
              return {
                ...mapped,
                timeEntryId: current.timeEntryId,
              };
            }
          }
          return mapped;
        })
        .filter(notEmpty);

      return saveProjectTimeEntriesMutation({
        variables: {
          input: {
            date: toISODateOnly(new Date(current.start.date)),
            timesheetId: current.timeSheetId,
            timeEntries: timeEntriesToSave.map(mapToTimeEntryInput),
          },
          ...timesheetsRange(),
        },
      })
        .then(() => {
          setCurrentProjectTimeEntriesMinutes(null);
          initialProjectEntriesRef.current = timesTracked;
          addSuccess(t(`${i18prefix}.projectTimeEntries.saveSuccess`));
        })
        .catch(errorHandler);
    },
    [
      saveProjectTimeEntriesMutation,
      current.start.date,
      current.timeSheetId,
      current.timeEntryId,
      errorHandler,
      addSuccess,
      t,
    ],
  );

  const [showProjectTimeEntriesForm, setShowProjectTimeEntriesForm] =
    useState<boolean>(false);

  const hasProjectTimeEntries = mappedProjectTimeEntries.length > 0;

  useEffect(() => {
    if (hasProjectTimeEntries) {
      setShowProjectTimeEntriesForm(true);
    }
  }, [hasProjectTimeEntries]);

  const canShowProjectTimeEntries =
    projects &&
    (hasProjectTimeEntries || showProjectTimeEntriesForm) &&
    handleSaveProjectTimeEntries;

  const showConnectToProjectsButton =
    isTimeTrackingWithProjects &&
    !readonly &&
    projects &&
    projects.length > 0 &&
    !hasProjectTimeEntries &&
    !showProjectTimeEntriesForm;

  const handleConnectToProjects = useCallback(() => {
    setShowProjectTimeEntriesForm(true);
  }, []);

  const handleCancelProjectTimeEntries = useCallback(() => {
    if (!hasProjectTimeEntries) {
      setShowProjectTimeEntriesForm(false);
    }
  }, [hasProjectTimeEntries]);

  const { timeEntryStats } = useAutoBreaksLogic({
    timeEntry: current,
    autoBreakRules,
    validationErrors,
  });

  const initialHours = useMemo(() => {
    if (!hasProjectTimeEntries && showProjectTimeEntriesForm) {
      return minToHoursInput(timeEntryStats.workingMinutes);
    }
    return undefined;
  }, [
    hasProjectTimeEntries,
    showProjectTimeEntriesForm,
    timeEntryStats.workingMinutes,
  ]);

  const validationMessages = getValidationErrorMessages(validationErrors);
  const submitDisabledTitle = getSubmitDisabledTitle(
    validationMessages,
    isProcessing || isSubmitting,
    t,
  );

  const timeEntryButtons =
    !readonly && isDirty ? (
      <TimeEntryEmployeeButtons
        onCancel={handleCancel}
        isProcessing={isProcessing}
        submitDisabled={
          isProcessing || isSubmitting || validationErrors.size > 0
        }
        submitDisabledTitle={submitDisabledTitle}
      />
    ) : null;

  const newTimeEntry = current.timeEntryId === null;

  return (
    <Container>
      <TimeEntryForm onSubmit={handleSubmit} onCancel={handleCancel}>
        <TimeEntryCardLayout
          isProcessing={isProcessing}
          highlight={isDirty}
          buttons={timeEntryButtons}>
          <TimeEntryDumb
            timeEntry={current}
            costCenters={costCenters}
            validationErrors={validationErrors}
            readonly={readonly}
            readonlyReasons={readonlyReasons}
            notesVisible={notesVisible}
            costCenterVisible={costCenterVisible}
            addNotesButtonVisible={!notesVisible}
            addAssignCostCenterButtonVisible={
              costCenters.length > 0 && !costCenterVisible
            }
            isDeleteBreakAllowed={false}
            isDeleteTimeEntryAllowed={false}
            isEditNotesAllowed={true}
            forceShowEndDate={false}
            autoBreakRules={autoBreakRules}
            isDeleteChangeRequestAllowed={isDeleteChangeRequestAllowed}
            onDeleteChangeRequestClick={handleDeleteClick}
            dispatch={dispatch}
            onAddNewTimeEntryClick={onAddNewTimeEntryClick}
            newTimeEntry={newTimeEntry}
            isLastEntry={isLastEntry}
            restrictedFutureEntries={restrictedFutureEntries}
            projects={projects}
            timeEntriesForDay={timeEntriesForDay}
            showTimeEntries={showTimeEntries}
            isTimeTrackingWithProjects={isTimeTrackingWithProjects}
            restrictionReason={restrictionReason}
            showRestrictedWarningModal={showRestrictedWarningModal}
            onShowRestrictedWarningModal={setShowRestrictedWarningModal}
            onAddBreak={handleAddBreak}
            mappedProjectTimeEntries={mappedProjectTimeEntries}
            totalProjectTimeEntriesMinutes={totalProjectTimeEntriesMinutes}
            currentProjectTimeEntriesMinutes={currentProjectTimeEntriesMinutes}
            onProjectTimeEntriesMinutesChangeInternal={
              handleProjectTimeEntriesMinutesChangeInternal
            }
            onSaveProjectTimeEntries={handleSaveProjectTimeEntries}
            projectEntriesFormRef={projectEntriesFormRef}
            extraFieldsRef={extraFieldsRef}
            isSavingProjectTimeEntries={isSavingProjectTimeEntries}
            showProjectTimeEntriesForm={showProjectTimeEntriesForm}
            onShowProjectTimeEntriesForm={setShowProjectTimeEntriesForm}
            onConnectToProjects={handleConnectToProjects}
            onCancelProjectTimeEntries={handleCancelProjectTimeEntries}
            canShowProjectTimeEntries={!!canShowProjectTimeEntries}
            showConnectToProjectsButton={!!showConnectToProjectsButton}
            timeEntryStats={timeEntryStats}
            initialHours={initialHours}
            onProjectEntriesChange={setCurrentProjectEntries}
            projectEntriesResetKey={projectEntriesResetKey}
            employeeId={meId}
            canEditExtraFields={true}
            extraFieldsObjectApiName={
              ExtraFieldsSupportedObject.TimeEntryChangeRequest
            }
            extraFieldsFormId={ExtraFieldsFormId.TimeEntryChangeRequestEdit}
          />
        </TimeEntryCardLayout>
        <ConfirmModalSimple
          visible={!!deleteTimeEntryChangeRequestId}
          onClose={handleDeleteConfirmationClose}
          onSubmit={handleDeleteConfirmationSubmit}
        />
      </TimeEntryForm>
    </Container>
  );
};

const Container = styled('div')({
  marginBottom: '2.2rem',
});

export default TimeEntryEmployeeSmart;
